import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BfxInputDirective } from "./bfx-input";
import { InputLabelMixin, InputLabelMixinInputs } from "./mixins/input-label";
import { ComponentMixin } from "./mixins/mixin";
import { RequiredMixinInputs, RequiredMixin } from "./mixins/required";
import { SizeMixin, SizeMixinInputs } from "./mixins/size";
import { EmphasisMixin } from "./mixins/emphasis";
import { BfxFieldTextDirective } from "./bfx-field-text";
import { FieldIconsMixin, FieldIconsMixinInputs } from "./mixins/field-icons";
import { FieldStateMixin, FieldStateMixinInputs } from "./mixins/field-state";
import { InputLabelStateMixin, InputLabelStateMixinInputs } from "./mixins/label-state";
import { DisabledMixin, DisabledMixinInputs } from "./mixins/disabled";
import { PasswordMixin, PasswordMixinInputs } from "./mixins/password";
import { MaxLengthMixinInputs, MaxLengthMixin } from "./mixins/max-length";
import { PlaceholderMixin, PlaceholderMixinInputs } from "./mixins/placeholder";
import { InputtedMixin, InputtedMixinInputs } from "./mixins/inputted";
import { ExternalStateMixin, ExternalStateMixinInputs } from "./mixins/external-state";
import { BfxInputControlStateEnum } from "./models/base-components.enums";
import { Subscription, distinctUntilChanged } from "rxjs";

export const TEXT_INPUT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxTextInputComponent),
  multi: true,
};

const Mixins = ExternalStateMixin(
  InputLabelMixin(
    EmphasisMixin(
      SizeMixin(
        FieldIconsMixin(
          FieldStateMixin(
            InputLabelStateMixin(
              InputtedMixin(
                PlaceholderMixin(
                  PasswordMixin(MaxLengthMixin(RequiredMixin(DisabledMixin(ComponentMixin)))),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
);

/**
 * Text input field - currently the title of the input shows the input value - all
 * other space in the component uses the component title
 */
@Component({
  selector: "bfx-text-input",
  templateUrl: "./html/bfx-text-input.html",
  providers: [TEXT_INPUT_VALUE_ACCESSOR],
  inputs: [
    ...DisabledMixinInputs,
    ...ExternalStateMixinInputs,
    ...FieldStateMixinInputs,
    ...FieldIconsMixinInputs,
    ...InputLabelMixinInputs,
    ...InputLabelStateMixinInputs,
    ...InputtedMixinInputs,
    ...MaxLengthMixinInputs,
    ...PasswordMixinInputs,
    ...PlaceholderMixinInputs,
    ...RequiredMixinInputs,
    ...SizeMixinInputs,
  ],
  outputs: ["fieldIconClick"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxTextInputComponent
  extends Mixins
  implements OnDestroy, OnInit, ControlValueAccessor
{
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChangeFn = (_: any) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouchedFn = () => {};

  public value: string | null = null;

  @ViewChild("text") textElement: ElementRef;

  public cd: ChangeDetectorRef;

  @Input() inputStartContainer: TemplateRef<Element> = null;
  @Input() inputEndContainer: TemplateRef<Element> = null;
  @Input() fieldStartContainer: TemplateRef<Element> = null;
  @Input() fieldEndContainer: TemplateRef<Element> = null;

  @Input() public hideField: boolean = false;

  @Input() public placeholder: string = null;

  externalStateSubscription: Subscription;

  @Output("focus") focusEmitter = new EventEmitter();
  @Output("blur") blurEmitter = new EventEmitter();

  constructor(
    public injector: Injector,
    public inputDirective: BfxInputDirective,
    public fieldDirective: BfxFieldTextDirective,
  ) {
    super(injector);
    this.cd = injector.get(ChangeDetectorRef);
  }

  focus() {
    this.textElement?.nativeElement?.focus();
  }

  writeValue(value: string | null): void {
    if (this.textElement?.nativeElement instanceof HTMLInputElement) {
      this.textElement.nativeElement.value = value;
    }

    this.value = value;
    this.inputted = this.value != null && this.value !== "";
    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.fieldState = this.disabled ? "disabled" : "enabled";
    this.cd.markForCheck();
  }

  ngOnInit() {
    this.inputLabelState = this.defaultState;
    this.fieldState = this.defaultState;
    this.externalStateSubscription = this.externalStateChange
      .pipe(distinctUntilChanged())
      .subscribe((_) => {
        if (this.fieldState != "active") {
          this.fieldState = this.defaultState;
        }
        this.cd.markForCheck();
      });
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    if (this.externalStateSubscription != null) {
      this.externalStateSubscription.unsubscribe();
      this.externalStateSubscription = null;
    }
  }

  get defaultState(): "enabled" | "disabled" | "invalid" {
    return this.disabled
      ? "disabled"
      : this.externalState === BfxInputControlStateEnum.INVALID
        ? "invalid"
        : "enabled";
  }

  onPointerOver(_) {
    if (this.fieldState != "active" && this.fieldState != "disabled") {
      this.fieldState = "hover";
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    }
  }

  onPointerOut(_) {
    if (this.fieldState != "active") {
      this.fieldState = this.defaultState;
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    }
  }

  onFocus(_) {
    if (this.fieldState != "disabled") {
      this.fieldState = "active";
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    }
    this.focusEmitter.next(_);
  }

  onBlur(_) {
    this.fieldState = this.disabled
      ? "disabled"
      : this.externalState === BfxInputControlStateEnum.INVALID
        ? "invalid"
        : "enabled";
    this.fieldStateChange.emit(this.fieldState);
    this.cd.markForCheck();
    this.blurEmitter.next(_);
  }

  public onInput(event: { target: HTMLInputElement }) {
    this.onChange(event);
  }

  public onChange(event: { target: HTMLInputElement }) {
    this.value = event.target.value;
    this.onChangeFn(this.value);
    this.inputted = this.value != null && this.value !== "";
    this.cd.markForCheck();
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxFieldText(): Record<string, any> {
    return this.fieldDirective.directiveHost(this);
  }

  public onFieldIconClick(event: MouseEvent, type: "start" | "end", index: number) {
    this.fieldIconClick.next({ event, type, index });
  }
}
